import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass, X, NotePencil, CheckSquare, CircleNotch } from "@phosphor-icons/react";
import api from "../lib/api";

// Live cross-module search (Notes + Tasks/Schedule) rendered in the top nav
// bar on every page. Collapses to a plain icon button when idle so it never
// steals width from the module tabs, expands into an input + results
// dropdown on click. Debounced to avoid hammering the API on every
// keystroke - this backs a small dropdown, not a dedicated results page.
export function GlobalSearchBox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ notes: [], tasks: [] });
  const [loading, setLoading] = useState(false);
  // The dropdown is rendered `position: fixed` (see dropdownStyle below)
  // instead of `absolute` because ModuleNavBar has `overflow-x-auto` on
  // the nav element - per the CSS spec, setting only one overflow axis
  // forces the other to behave as `auto` too, which silently clips any
  // absolutely-positioned child that extends below the bar (the dropdown
  // was rendering but invisible/clipped). `fixed` escapes that ancestor
  // overflow entirely since it's positioned relative to the viewport.
  const [dropdownCoords, setDropdownCoords] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const updateCoords = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) setDropdownCoords({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };
    updateCoords();
    window.addEventListener("resize", updateCoords);
    return () => window.removeEventListener("resize", updateCoords);
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (!trimmed) {
      setResults({ notes: [], tasks: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      try {
        const { data } = await api.get("/search", { params: { q: trimmed } });
        if (requestId === requestIdRef.current) setResults(data);
      } catch (e) {
        // Search is a non-critical convenience feature - fail silently.
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const closeAndReset = () => {
    setOpen(false);
    setQuery("");
    setResults({ notes: [], tasks: [] });
  };

  const goToNote = (note) => {
    navigate(`/dashboard?openNote=${note.id}`);
    closeAndReset();
  };

  const goToTask = (task) => {
    // Scheduled tasks (due_date set) only render on the Schedule calendar -
    // TasksPage deliberately hides them (it's a backlog of unscheduled
    // work), so a scheduled result has to deep-link into Schedule instead.
    if (task.due_date) navigate(`/schedule?openTask=${task.id}&date=${task.due_date}`);
    else navigate(`/tasks?openTask=${task.id}`);
    closeAndReset();
  };

  const trimmedQuery = query.trim();
  const hasResults = results.notes.length > 0 || results.tasks.length > 0;

  return (
    <div ref={containerRef} className="relative">
      {open ? (
        <div className="flex items-center gap-1.5 border border-border bg-background px-2 h-7 w-40 sm:w-52 md:w-64" data-testid="global-search-input-wrapper">
          <MagnifyingGlass size={13} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && closeAndReset()}
            placeholder={t("search.placeholder")}
            className="flex-1 bg-transparent outline-none text-sm font-semibold min-w-0"
          />
          <button
            type="button"
            data-testid="global-search-close-button"
            onClick={closeAndReset}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X size={13} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="global-search-open-button"
          onClick={() => setOpen(true)}
          title={t("search.placeholder")}
          className="flex items-center justify-center h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          <MagnifyingGlass size={15} />
        </button>
      )}

      {open && trimmedQuery && dropdownCoords && (
        <div
          className="fixed w-72 md:w-80 max-h-[70vh] overflow-y-auto border border-border bg-card shadow-lg z-50"
          style={{ top: dropdownCoords.top, right: dropdownCoords.right }}
          data-testid="global-search-results"
        >
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground" data-testid="global-search-loading">
              <CircleNotch size={14} className="animate-spin" /> {t("search.searching")}
            </div>
          ) : !hasResults ? (
            <p className="px-3 py-3 text-sm text-muted-foreground" data-testid="global-search-empty">{t("search.noResults")}</p>
          ) : (
            <>
              {results.notes.length > 0 && (
                <div>
                  <p className="px-3 pt-2 pb-1 text-xs font-mono-tag font-bold text-muted-foreground">{t("search.notesGroupLabel")}</p>
                  {results.notes.map((note) => (
                    <button
                      key={note.id}
                      type="button"
                      data-testid={`global-search-result-note-${note.id}`}
                      onClick={() => goToNote(note)}
                      className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-secondary transition-colors"
                    >
                      <NotePencil size={14} className="text-primary shrink-0 mt-0.5" />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold truncate">{note.title || t("captureCard.untitledFallback")}</span>
                        {note.snippet && <span className="block text-xs text-muted-foreground truncate">{note.snippet}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {results.tasks.length > 0 && (
                <div>
                  <p className="px-3 pt-2 pb-1 text-xs font-mono-tag font-bold text-muted-foreground">{t("search.tasksGroupLabel")}</p>
                  {results.tasks.map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      data-testid={`global-search-result-task-${task.id}`}
                      onClick={() => goToTask(task)}
                      className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-secondary transition-colors"
                    >
                      <CheckSquare size={14} className={`shrink-0 mt-0.5 ${task.completed ? "text-muted-foreground" : "text-primary"}`} />
                      <span className="min-w-0">
                        <span className={`block text-sm font-semibold truncate ${task.completed ? "line-through text-muted-foreground" : ""}`}>{task.title}</span>
                        {task.due_date && (
                          <span className="block text-xs text-muted-foreground truncate">{task.due_date}{task.due_time ? ` ${task.due_time}` : ""}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
